import { inject, Injectable } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { sum } from "es-toolkit";
import { groupBy } from "lodash";
import { defer, iif, lastValueFrom, Observable } from "rxjs";
import { map } from "rxjs/operators";
import { InvoiceBackend } from "src/app/backend/api/invoice.backend";
import {
  CustomerLightPayingLater,
  CustomerLightPayingLaterPrintViewModel
} from "src/app/backend/models/customer/customer";
import { InvoiceLight } from "src/app/backend/models/invoice/invoice";
import { BootstrapService } from "src/app/modules/bootstrap/bootstrap.service";
import { RegionService } from "src/app/services/feature-access-control/region.service";
import { PrintService } from "src/app/services/print/print.service";
import { TemplateService } from "src/app/services/template.service";
import { formatCurrency } from "src/app/support/currency";
import { getCustomerNominative } from "src/app/support/customer";
import { dateUtil } from "src/app/support/date-util";
import { sntTranslate } from "src/app/support/translate";

@Injectable()
export class PayingLaterCustomerBackend {
  readonly #invoiceBackend = inject(InvoiceBackend);
  readonly #bootstrapService = inject(BootstrapService);
  readonly #printService = inject(PrintService);
  readonly #templateService = inject(TemplateService);
  readonly #regionService = inject(RegionService);

  hasDebts({ customerId }: { customerId: string }): Observable<boolean> {
    return this.listByCustomer({ customerId }).pipe(
      map((customers: CustomerLightPayingLater[]) => {
        return customers.length > 0;
      })
    );
  }

  listByCustomer({
    customerId
  }: {
    customerId: string;
  }): Observable<CustomerLightPayingLater[]> {
    return this.#list({ customerId });
  }

  listAll(): Observable<CustomerLightPayingLater[]> {
    return this.#list({});
  }

  #listExpiredByCustomer({
    customerId
  }: {
    customerId: string;
  }): Observable<CustomerLightPayingLater[]> {
    return this.#invoiceBackend.listExpiredByCustomerLight({ customerId }).pipe(
      map((invoices: InvoiceLight[]) => {
        if (invoices.length === 0) {
          return [];
        }

        const customer = invoices[0].customer;
        const totalBilled: number = invoices.reduce(
          (total: number, invoice: InvoiceLight) => {
            return total + (invoice.adjustedTotal || 0);
          },
          0
        );
        const totalOutstanding: number = invoices.reduce(
          (total: number, invoice: InvoiceLight) => {
            return total + (invoice.toBePaid || 0);
          },
          0
        );
        const totalCollected: number = totalBilled - totalOutstanding;

        return [
          <CustomerLightPayingLater>{
            ...customer,
            invoices,
            totalBilled,
            totalCollected,
            totalOutstanding
          }
        ];
      })
    );
  }

  #list({
    customerId
  }: {
    customerId?: string;
  }): Observable<CustomerLightPayingLater[]> {
    return this.#invoiceBackend.listAllUnpaidLight({ customerId }).pipe(
      map((invoices: InvoiceLight[]) => {
        return invoices.filter((invoice: InvoiceLight) => {
          return (invoice.toBePaid || 0) > 0.2;
        });
      }),
      map((invoices: InvoiceLight[]) => {
        const customers: Record<string, InvoiceLight[]> = groupBy(
          invoices.filter((invoice: InvoiceLight) => !!invoice.customer),
          (invoice: InvoiceLight) => {
            return invoice.customer?.id;
          }
        );

        return Object.keys(customers).map((customerId: string) => {
          const invoices: InvoiceLight[] = customers[customerId];
          const customer = invoices[0].customer;

          const totalBilled: number = invoices.reduce(
            (total: number, invoice: InvoiceLight) => {
              return total + (invoice.adjustedTotal || 0);
            },
            0
          );

          const totalOutstanding: number = invoices.reduce(
            (total: number, invoice: InvoiceLight) => {
              return total + (invoice.toBePaid || 0);
            },
            0
          );

          const totalCollected: number = totalBilled - totalOutstanding;

          return <CustomerLightPayingLater>{
            ...customer,
            invoices: invoices,
            totalBilled,
            totalCollected,
            totalOutstanding
          };
        });
      })
    );
  }

  async printForCustomer({
    customerId
  }: {
    customerId: string;
  }): Promise<void> {
    await this.#printService.printPdf(this.#toPdf({ customerId }));
  }

  async printAll(): Promise<void> {
    await this.#printService.printPdf(this.#toPdf({}));
  }

  async #toPdf({ customerId }: { customerId?: string }): Promise<Blob> {
    const [customerPayingLaterTemplate, tailwindCss] = await Promise.all([
      fetch(
        "/assets/ejs-templates/customer-paying-later/customer-paying-later-report.ejs"
      ).then((response) => response.text()),
      fetch("/assets/ejs-templates/customer-paying-later/tailwind.css").then(
        (response) => response.text()
      )
    ]);

    const customers: CustomerLightPayingLater[] = await lastValueFrom(
      iif(
        () => !!customerId,
        defer(() => this.#listExpiredByCustomer({ customerId: customerId! })),
        defer(() => this.listAll())
      )
    );

    const viewModel: CustomerLightPayingLaterPrintViewModel =
      await this.#getPrintViewModel({ customers });

    const content = this.#templateService.render(customerPayingLaterTemplate, {
      ...viewModel,
      customerId,
      tailwindCss
    });

    let header: string;

    switch (true) {
      case !!customerId && customers.length > 0: {
        const customerNominative: string = getCustomerNominative(customers[0]);

        header = `
        <div style="padding: 16px; margin-left: 16px;">
          <h2 style="font-size: 22px;">${sntTranslate(
            marker("Fatture insolute di [nominative] al [date]"),
            {
              nominative: customerNominative,
              date: dateUtil().format("l")
            }
          )}</h2>
        </div>`;
        break;
      }
      default: {
        header = `
        <div style="padding: 16px; margin-left: 16px;">
          <h2 style="font-size: 22px;">${sntTranslate(
            marker("Clienti insoluti al [date]"),
            {
              date: dateUtil().format("l")
            }
          )}</h2>
        </div>`;
        break;
      }
    }

    return await this.#printService.htmlToPdf({
      header,
      footer: `
          <div
            style="
              height: 100%;
              flex: 1;
              display: flex;
              flex-direction: column;
              justify-content: start;
              padding-left: 8px;
              padding-right: 16px;
              gap: 2px;
              margin: 0;
            "
          >
            <div style="display: flex; justify-content: end; align-items: start">
              <div style="font-size: 8px">
                ${sntTranslate(marker("Pagina"))} <span class="pageNumber"></span> /
                <span class="totalPages"></span>
              </div>
            </div>
          </div>`,
      content,
      additionalOptions: {
        displayHeaderFooter: true
      }
    });
  }

  async #getPrintViewModel({
    customers
  }: {
    customers: CustomerLightPayingLater[];
  }): Promise<CustomerLightPayingLaterPrintViewModel> {
    const totalExpiredInvoices: number = sum(
      customers.map(
        (customer: CustomerLightPayingLater) => customer.invoices.length
      )
    );

    const totalOutstanding: number = sum(
      customers.map((customer: CustomerLightPayingLater) =>
        sum(
          customer.invoices.map((invoice: InvoiceLight) => {
            return invoice.toBePaid || 0;
          })
        )
      )
    );

    const totalBilled: number = sum(
      customers.map((customer: CustomerLightPayingLater) =>
        sum(
          customer.invoices.map((invoice: InvoiceLight) => {
            return invoice.adjustedTotal || 0;
          })
        )
      )
    );

    const totalCollected: number = sum(
      customers.map((customer: CustomerLightPayingLater) =>
        sum(
          customer.invoices.map((invoice: InvoiceLight) => {
            return invoice.paidTotal || 0;
          })
        )
      )
    );

    const totalReversed: number = sum(
      customers.map((customer: CustomerLightPayingLater) =>
        sum(
          customer.invoices.map((invoice: InvoiceLight) => {
            return invoiceReversedAmount(invoice);
          })
        )
      )
    );

    return <CustomerLightPayingLaterPrintViewModel>{
      rows: customers.map((customer: CustomerLightPayingLater) => {
        const customerTotalCollected: number = sum(
          customer.invoices.map((invoice: InvoiceLight) => {
            return invoice.paidTotal || 0;
          })
        );
        const customerTotalReversed: number = sum(
          customer.invoices.map((invoice: InvoiceLight) => {
            return invoiceReversedAmount(invoice);
          })
        );

        return {
          invoices: customer.invoices.map((invoice: InvoiceLight) => {
            const collected: number = invoice.paidTotal || 0;
            const reversed: number = invoiceReversedAmount(invoice);

            return {
              number: invoice.number,
              emittedAt: dateUtil(invoice.emittedAt).format("l"),
              expiredAt: dateUtil(invoice.expiredAt).format("l"),
              toBePaid: formatCurrency({
                value: invoice.toBePaid || 0,
                regionService: this.#regionService
              }),
              adjustedTotal: formatCurrency({
                value: invoice.adjustedTotal,
                regionService: this.#regionService
              }),
              collected: formatCurrency({
                value: collected,
                regionService: this.#regionService
              }),
              reversed: formatCurrency({
                value: reversed,
                regionService: this.#regionService
              })
            };
          }),
          customer: {
            nominative: getCustomerNominative(customer),
            email: customer.email,
            phone: customer.phone,
            ssn: customer.ssn,
            vatNumber: customer.vatNumber
          },
          totalOustanding: formatCurrency({
            value: sum(
              customer.invoices.map((invoice: InvoiceLight) => {
                return invoice.toBePaid || 0;
              })
            ),
            regionService: this.#regionService
          }),
          totalBilled: formatCurrency({
            value: customer.totalBilled,
            regionService: this.#regionService
          }),
          totalCollected: formatCurrency({
            value: customerTotalCollected,
            regionService: this.#regionService
          }),
          totalReversed: formatCurrency({
            value: customerTotalReversed,
            regionService: this.#regionService
          }),
          hasReversed: customerTotalReversed > 0
        };
      }),
      totalBilled: formatCurrency({
        value: totalBilled,
        regionService: this.#regionService
      }),
      totalCollected: formatCurrency({
        value: totalCollected,
        regionService: this.#regionService
      }),
      totalReversed: formatCurrency({
        value: totalReversed,
        regionService: this.#regionService
      }),
      totalOutstanding: formatCurrency({
        value: totalOutstanding,
        regionService: this.#regionService
      }),
      totalExpiredInvoices,
      totalPayingLaterCustomers: customers.length,
      hasReversed: totalReversed > 0,
      billedLabel: sntTranslate(marker("Fatturato")),
      emittedAtLabel: sntTranslate(marker("Emessa il")),
      expiredAtLabel: sntTranslate(marker("Scaduta il")),
      numberLabel: sntTranslate(marker("Numero")),
      toBePaidLabel: sntTranslate(marker("Insoluto")),
      totalExpiredLabel: sntTranslate(marker("Totale insoluto")),
      nominativeLabel: sntTranslate(marker("Nominativo")),
      expiredInvoicesLabel: sntTranslate(marker("Fatture insolute")),
      payingLaterCustomersLabel: sntTranslate(marker("Clienti insoluti")),
      collectedLabel: sntTranslate(marker("Incassato")),
      reversedLabel: sntTranslate(marker("Stornato")),
      totalCollectedLabel: sntTranslate(marker("Totale incassato")),
      totalReversedLabel: sntTranslate(marker("Totale stornato")),
      totalBilledLabel: sntTranslate(marker("Totale fatturato"))
    };
  }
}

function invoiceReversedAmount(invoice: InvoiceLight): number {
  if (invoice.reversedAmount !== undefined) {
    return invoice.reversedAmount;
  }

  const adjustedTotal: number = invoice.adjustedTotal || 0;
  const paidTotal: number = invoice.paidTotal || 0;
  const toBePaid: number = invoice.toBePaid || 0;

  return Math.max(0, adjustedTotal - paidTotal - toBePaid);
}
